import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import type { ReactNode } from "react";
import { removeAllTenantData } from "../api/queryInvalidation";
import { queryClient } from "../providers/QueryProvider";
import {
  getSelectedTenantId,
  setSelectedTenantIdInStorage,
} from "../utils/authStorage";

interface TenantContextType {
  selectedTenantId: number | null; // null means "Global"
  setSelectedTenantId: (
    tenantId: number | null | ((prev: number | null) => number | null),
  ) => void;
  isGlobal: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

/**
 * TenantProvider
 *
 * Manages the selected tenant context for the act-as-tenant feature.
 * Stores selected tenantId (or null for "Global") in **sessionStorage**
 * (per-tab) so that super-admin users can view different tenants
 * side-by-side in separate browser tabs (GitHub issue #24).
 */
export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [selectedTenantId, setSelectedTenantIdState] = useState<number | null>(
    () => getSelectedTenantId(),
  );

  // Track previous tenant so we can clear its cache on switch
  const prevTenantRef = useRef<number | null>(selectedTenantId);

  useEffect(() => {
    // Persist to sessionStorage (per-tab)
    setSelectedTenantIdInStorage(selectedTenantId);

    // Clear old tenant's cached data when switching tenants (ISO 27001 A.9.1.2)
    const prevTenant = prevTenantRef.current;
    if (prevTenant !== selectedTenantId && prevTenant !== null) {
      removeAllTenantData(queryClient, prevTenant);
    }
    prevTenantRef.current = selectedTenantId;
  }, [selectedTenantId]);

  const setSelectedTenantId = (
    tenantId: number | null | ((prev: number | null) => number | null),
  ) => {
    setSelectedTenantIdState(tenantId);
  };

  const value: TenantContextType = {
    selectedTenantId,
    setSelectedTenantId,
    isGlobal: selectedTenantId === null,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export default TenantProvider;
